"use client";

import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "next/navigation";
import { Eye, EyeOff, LogIn, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

type LoginVariant = "system" | "school";

interface LoginFormClientProps {
  variant: LoginVariant;
  schoolId?: string;
  redirectSlug?: string;
}

const FAILED_ATTEMPT_LIMIT = 3;
const CAPTCHA_LENGTH = 5;

type AuthSession = {
  user?: {
    role?: string;
  };
} | null;

type SignInResult = {
  error?: string;
  ok: boolean;
  status: number;
};

function storageKey(variant: LoginVariant, schoolId?: string) {
  return `login:${variant}:${schoolId ?? "global"}`;
}

function generateCaptcha() {
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let value = "";
  for (let i = 0; i < CAPTCHA_LENGTH; i += 1) {
    value += alphabet[Math.floor(Math.random() * alphabet.length)];
  }
  return value;
}

async function getSameOriginCsrfToken() {
  const response = await fetch("/api/auth/csrf", {
    credentials: "same-origin",
  });
  if (!response.ok) return "";

  const payload = (await response.json()) as { csrfToken?: string };
  return payload.csrfToken ?? "";
}

async function getSameOriginSession(): Promise<AuthSession> {
  const response = await fetch("/api/auth/session", {
    credentials: "same-origin",
    cache: "no-store",
  });
  if (!response.ok) return null;

  return (await response.json()) as AuthSession;
}

async function signInWithCredentials(input: {
  username: string;
  password: string;
  schoolId?: string;
}) {
  const csrfToken = await getSameOriginCsrfToken();
  if (!csrfToken) {
    return { error: "MissingCSRF", ok: false, status: 400 } satisfies SignInResult;
  }

  const body = new URLSearchParams({
    username: input.username,
    password: input.password,
    csrfToken,
    callbackUrl: window.location.href,
  });
  if (input.schoolId) {
    body.set("schoolId", input.schoolId);
  }

  const response = await fetch("/api/auth/callback/credentials", {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      "X-Auth-Return-Redirect": "1",
    },
    body,
    credentials: "same-origin",
  });

  const payload = (await response.json().catch(() => ({}))) as { url?: string };
  const error = payload.url ? new URL(payload.url, window.location.origin).searchParams.get("error") ?? undefined : undefined;

  return {
    error,
    ok: response.ok && !error,
    status: response.status,
  } satisfies SignInResult;
}

export function LoginFormClient({ variant, schoolId, redirectSlug }: LoginFormClientProps) {
  const searchParams = useSearchParams();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [captchaValue, setCaptchaValue] = useState("");
  const [captchaInput, setCaptchaInput] = useState("");
  const [hydrated, setHydrated] = useState(false);

  const captchaRequired = hydrated && failedAttempts >= FAILED_ATTEMPT_LIMIT;
  const storageBaseKey = useMemo(() => storageKey(variant, schoolId), [variant, schoolId]);
  const callbackUrl = searchParams.get("callbackUrl");
  const safeCallbackUrl = useMemo(() => {
    if (!callbackUrl) return "";
    if (typeof window === "undefined") return "";
    try {
      const url = new URL(callbackUrl, window.location.origin);
      if (url.origin !== window.location.origin) return "";
      if (url.pathname.endsWith("/login") || url.pathname === "/login") return "";
      return `${url.pathname}${url.search}${url.hash}`;
    } catch {
      return "";
    }
  }, [callbackUrl]);

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    const attemptKey = `${storageBaseKey}:attempts`;
    const captchaKey = `${storageBaseKey}:captcha`;
    const storedAttempts = Number(window.sessionStorage.getItem(attemptKey) ?? "0");
    const normalizedAttempts = Number.isFinite(storedAttempts) ? storedAttempts : 0;

    setFailedAttempts(normalizedAttempts);
    if (normalizedAttempts >= FAILED_ATTEMPT_LIMIT) {
      const storedCaptcha = window.sessionStorage.getItem(captchaKey);
      const nextCaptcha = storedCaptcha || generateCaptcha();
      if (!storedCaptcha) {
        window.sessionStorage.setItem(captchaKey, nextCaptcha);
      }
      setCaptchaValue(nextCaptcha);
    }

    setHydrated(true);
  }, [storageBaseKey]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const persistAttempts = (attempts: number) => {
    const attemptKey = `${storageBaseKey}:attempts`;
    const captchaKey = `${storageBaseKey}:captcha`;
    window.sessionStorage.setItem(attemptKey, String(attempts));
    setFailedAttempts(attempts);

    if (attempts >= FAILED_ATTEMPT_LIMIT) {
      let nextCaptcha = window.sessionStorage.getItem(captchaKey);
      if (!nextCaptcha) {
        nextCaptcha = generateCaptcha();
        window.sessionStorage.setItem(captchaKey, nextCaptcha);
      }
      setCaptchaValue(nextCaptcha);
    } else {
      window.sessionStorage.removeItem(captchaKey);
      setCaptchaValue("");
      setCaptchaInput("");
    }
  };

  const resetLoginState = () => {
    window.sessionStorage.removeItem(`${storageBaseKey}:attempts`);
    window.sessionStorage.removeItem(`${storageBaseKey}:captcha`);
    setFailedAttempts(0);
    setCaptchaValue("");
    setCaptchaInput("");
    setError("");
  };

  const refreshCaptcha = () => {
    const nextCaptcha = generateCaptcha();
    window.sessionStorage.setItem(`${storageBaseKey}:captcha`, nextCaptcha);
    setCaptchaValue(nextCaptcha);
    setCaptchaInput("");
  };

  const recordFailedLogin = () => {
    const storedAttempts = Number(
      window.sessionStorage.getItem(`${storageBaseKey}:attempts`) ?? failedAttempts,
    );
    const baseAttempts = Number.isFinite(storedAttempts) ? storedAttempts : failedAttempts;
    const nextAttempts = baseAttempts + 1;

    persistAttempts(nextAttempts);
    setError(
      nextAttempts >= FAILED_ATTEMPT_LIMIT
        ? "帳號或密碼錯誤，請重新輸入驗證碼後再試"
        : "帳號或密碼錯誤，或無權限登入",
    );
    if (nextAttempts >= FAILED_ATTEMPT_LIMIT) {
      setCaptchaInput("");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (captchaRequired) {
      if (!captchaInput.trim()) {
        setError("請輸入驗證碼");
        return;
      }
      if (captchaInput.trim().toUpperCase() !== captchaValue) {
        setError("驗證碼錯誤，請重新輸入");
        refreshCaptcha();
        return;
      }
    }

    setLoading(true);

    try {
      const result = await signInWithCredentials({
        username,
        password,
        schoolId: variant === "school" ? schoolId : undefined,
      });

      if (!result.ok || result.error) {
        recordFailedLogin();
        return;
      }

      const session = await getSameOriginSession();
      const role = session?.user?.role;

      if (!role) {
        recordFailedLogin();
        return;
      }

      resetLoginState();

      if (variant === 'system') {
        window.location.href = '/admin/schools';
        return;
      }

      if (safeCallbackUrl) {
        window.location.href = safeCallbackUrl;
        return;
      }

      if (role === 'super_admin') {
        window.location.href = '/admin/schools';
      } else if (role === 'judge') {
        window.location.href = `/${redirectSlug ?? schoolId}/judge`;
      } else if (redirectSlug ?? schoolId) {
        window.location.href = `/${redirectSlug ?? schoolId}`;
      } else {
        window.location.href = '/';
      }
    } catch {
      recordFailedLogin();
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="card p-6 space-y-4">
      <div>
        <label htmlFor="username" className="mb-1.5 block text-sm font-semibold text-foreground">
          帳號
        </label>
        <input
          id="username"
          type="text"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          required
          placeholder="請輸入帳號"
          className="input-field"
        />
      </div>

      <div>
        <label htmlFor="password" className="mb-1.5 block text-sm font-semibold text-foreground">
          密碼
        </label>
        <div className="relative">
          <input
            id="password"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            placeholder="請輸入密碼"
            className="input-field pr-11"
          />
          <button
            type="button"
            onClick={() => setShowPassword((prev) => !prev)}
            className="absolute inset-y-0 right-0 flex items-center px-3 text-text-muted transition-colors hover:text-foreground"
            aria-label={showPassword ? "隱藏密碼" : "顯示密碼"}
          >
            {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
          </button>
        </div>
      </div>

      {captchaRequired && (
        <div className="space-y-2 rounded-xl border border-border bg-surface p-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-foreground">驗證碼</p>
              <p className="text-xs text-text-muted">連續 3 次登入失敗後需要輸入驗證碼</p>
            </div>
            <button
              type="button"
              onClick={refreshCaptcha}
              className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs font-medium text-text-secondary transition-colors hover:bg-muted hover:text-foreground"
            >
              <RefreshCw className="size-3.5" />
              重整
            </button>
          </div>
          <div className="flex items-center justify-between gap-3 rounded-lg border border-dashed border-border px-3 py-2">
            <span className="font-mono text-base font-bold tracking-[0.35em] text-foreground">
              {captchaValue}
            </span>
            <span className="text-xs text-text-muted">請輸入右側文字</span>
          </div>
          <input
            type="text"
            value={captchaInput}
            onChange={(e) => setCaptchaInput(e.target.value)}
            placeholder="輸入驗證碼"
            className="input-field font-mono tracking-[0.25em] uppercase"
            autoComplete="off"
            inputMode="text"
          />
        </div>
      )}

      {error && (
        <div className="rounded-lg bg-destructive-surface px-4 py-2.5 text-sm font-medium text-destructive border border-destructive/20">
          {error}
        </div>
      )}

      <Button type="submit" className="w-full shadow-md" disabled={loading}>
        <LogIn className="size-4" />
        {loading ? "登入中..." : "登入"}
      </Button>
    </form>
  );
}
